import { Context } from './context'
import { ParentCategoryInfo } from '../preact-components/mega-menu/Types'
import { getCategoryAttachments } from './attachmentRepository'

// TODO: These should be API calls

/**
```
  // src/InSided/Doctrine/Entity/Forum.php
  TYPE_DEFAULT                  = 0;
  TYPE_KNOWLEDGE_BASE           = 1;
  TYPE_IDEATION                 = 2;
  TYPE_NEWS                     = 3;
```
*/
export enum LegacyCategoryType {
  default = 0,
  knowledgeBase = 1,
  ideation = 2,
  news = 3
}

const allTypes: LegacyCategoryType[] = [
  LegacyCategoryType.default,
  LegacyCategoryType.knowledgeBase,
  LegacyCategoryType.ideation,
  LegacyCategoryType.news
]

export async function getCategories(ctx: Context, types: LegacyCategoryType[] = allTypes) {
  const { prismaClient, userPermissions } = ctx
  const typeFilter = types.includes(LegacyCategoryType.default)
    ? { OR: [{ type: { in: types } }, { type: null }] }
    : { type: { in: types } }

  const categories = await prismaClient.forum.findMany({
    where: {
      AND: [
        {
          status: { not: 'purged' }
        },
        typeFilter,
        {
          forumid: { in: userPermissions.readableCategories }
        }
      ]
    },
    orderBy: {
      displayorder: 'asc'
    },
    select: {
      title: true,
      forumid: true,
      threadcount: true,
      displayorder: true,
      description: true,
      type: true,
      show_description_on_homepage: true,
      replycount: true,
      forum: {
        select: {
          title: true,
          forumid: true,
          threadcount: true,
          displayorder: true,
          type: true,
          description: true,
          show_description_on_homepage: true,
          replycount: true
        }
      }
    }
  })

  return categories
}

function isCategoryInfo(c: ParentCategoryInfo | undefined): c is ParentCategoryInfo {
  return !!c
}

interface ForumFields {
  forumid: number
  title: string
  type: number | null
  displayorder: number
  threadcount: number
  description: string | null
  replycount: number
  show_description_on_homepage: boolean
}

interface Forum extends ForumFields {
  forum: ForumFields | null
}

export async function prepCategories(ctx: Context, forums: Forum[]): Promise<ParentCategoryInfo[]> {
  const attachments = await getCategoryAttachments(
    ctx,
    forums.map((forum) => forum.forumid)
  )
  const tree = forums.reduce<Record<string, ParentCategoryInfo | undefined>>((idMap, category) => {
    const parentCategory = category.forum
    if (!parentCategory) return idMap

    let parent = idMap[parentCategory.forumid]
    if (!parent) {
      parent = idMap[parentCategory.forumid] = {
        id: String(parentCategory.forumid),
        title: parentCategory.title,
        type: parentCategory.type || 0,
        displayOrder: parentCategory.displayorder,
        url: `${parentCategory.title.toLowerCase().replace(' ', '-')}-${parentCategory.forumid}`,
        topicCount: parentCategory.threadcount,
        subforums: [],
        description: parentCategory.description || '',
        replyCount: parentCategory.replycount,
        showDescriptionOnHomepage: parentCategory.show_description_on_homepage,
        thumbnailUrl: getAttachmentUrl(parentCategory.forumid, 'Thumbnail image', attachments, ctx),
        heroImageUrl: getAttachmentUrl(parentCategory.forumid, 'Subforum image', attachments, ctx)
      }
    }
    if (!parent?.subforums) return idMap

    parent.subforums.push({
      id: String(category.forumid),
      title: category.title,
      type: category.type || 0,
      displayOrder: category.displayorder,
      url: `/${category.title.toLowerCase().replace(' ', '-')}-${category.forumid}`,
      topicCount: category.threadcount,
      description: category.description || '',
      replyCount: category.replycount,
      showDescriptionOnHomepage: category.show_description_on_homepage,
      thumbnailUrl: getAttachmentUrl(category.forumid, 'Thumbnail image', attachments, ctx),
      heroImageUrl: getAttachmentUrl(category.forumid, 'Subforum image', attachments, ctx)
    })
    return idMap
  }, {})

  return Object.values(tree)
    .filter(isCategoryInfo)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
}

function getAttachmentUrl(
  forumId: number,
  imageLabel: string,
  attachments: Array<{
    content_id: number
    label: string
    source_thumb: string | null
  }>,
  ctx: Context
): string {
  const source =
    attachments.find((a) => a.content_id === forumId && a.label === imageLabel)?.source_thumb || ''
  if (!source) return ''
  return `${ctx.config.config.params.uploadCdn || ''}${source}`
}

export async function getById (ctx: Context, forumid: number) {
  const { prismaClient, userPermissions } = ctx
  if (!userPermissions.readableCategories.includes(forumid)) {
    return null
  }

  return await prismaClient.forum.findFirst({
    where: {
      forumid
    }
  })
}
